// Pure rules behind the chef's Today screen and the plan's step strip
// (kitchen flow, Batch 4): which of the five steps a plan is on, what its one
// next step is, and what needs the chef now. No React, no API, no strings
// (todayStrings.ts words them). Tested in todayLogic.test.ts.
import type { PlanStatus } from './planTypes'
import type { KitchenToday, KitchenTodayBasic, KitchenTodayPlan, KitchenTodayRequest } from './todayTypes'

/** Planned → Stock asked → Stock here → Cooking → Wrapped up. */
export const PLAN_STEPS = ['planned', 'asked', 'here', 'cooking', 'wrapped'] as const
export type PlanStep = (typeof PLAN_STEPS)[number]

/** What a plan needs to know about itself for its steps. Both the Today
 *  summary (with line counts) and a full plan (status only) fit. */
export interface StepInput {
  status: PlanStatus
  sent_at?: string | null
  wrapped: boolean
  requisition_id?: string | null
  requisition_status?: string | null
  request?: KitchenTodayRequest | null
}

/**
 * Where the plan's stock is:
 *  none          not sent to the store yet
 *  nothing-asked sent, but the kitchen had everything (no request)
 *  waiting       asked, the store has sent nothing yet
 *  partly        some items sent and checked in, more still to come
 *  arrived       the store sent stock that is not checked in yet
 *  here          everything the store will send is checked in
 *  refused       the store turned the request down (or it was cancelled)
 */
export type StockState = 'none' | 'nothing-asked' | 'waiting' | 'partly' | 'arrived' | 'here' | 'refused'

export function stockState(p: StepInput): StockState {
  if (!p.sent_at) return 'none'
  const r = p.request
  if (r && r.lines > 0) {
    if (r.awaiting_receipt) return 'arrived'
    if (r.here + r.not_coming >= r.lines) return 'here'
    return r.sent === 0 ? 'waiting' : 'partly'
  }
  if (!p.requisition_id) return 'nothing-asked'
  switch (p.requisition_status) {
    case 'received':
    case 'closed':
      return 'here'
    case 'issued':
      return 'arrived'
    // Some sent, more to come — whether the sent part is checked in is not
    // known from the status alone (the Requests tab shows it).
    case 'partially_issued':
      return 'partly'
    case 'rejected':
    case 'cancelled':
      return 'refused'
    default:
      return 'waiting'
  }
}

const stockIn = (s: StockState) => s === 'here' || s === 'nothing-asked'

/** How many of the five steps are done (0–5). The step after them is "now". */
export function stepsDone(p: StepInput): number {
  if (p.wrapped) return 5
  switch (p.status) {
    case 'draft':
    case 'cancelled':
      return 0
    case 'confirmed':
      return 1
    case 'requisitioned':
      return stockIn(stockState(p)) ? 3 : 2
    case 'in_production':
      return 3
    case 'done':
      return 4
    default:
      return 0
  }
}

/**
 * The one next step:
 *  confirm        lock the plan (a draft)
 *  send-to-store  ask the store (locked, or cooking started before sending)
 *  check-in       stock arrived from the store — check it in
 *  wait           the store is still sending (nothing for the chef to do)
 *  start          everything is here — start cooking
 *  wrap-up        cooking (or the event day has passed) — say what became of the stock
 * null when there is nothing left to do (cancelled, wrapped up).
 */
export type NextStep = 'confirm' | 'send-to-store' | 'check-in' | 'wait' | 'start' | 'wrap-up'

export function nextStep(p: StepInput & { for_date?: string }, today?: string): NextStep | null {
  if (p.wrapped || p.status === 'cancelled') return null
  const past = !!today && !!p.for_date && p.for_date < today
  const stock = stockState(p)
  switch (p.status) {
    case 'draft':
      return 'confirm'
    case 'confirmed':
      return 'send-to-store'
    case 'requisitioned':
      if (stock === 'arrived') return 'check-in'
      if (past) return 'wrap-up'
      return stockIn(stock) ? 'start' : 'wait'
    case 'in_production':
      return p.sent_at ? 'wrap-up' : 'send-to-store'
    case 'done':
      return 'wrap-up'
    default:
      return null
  }
}

/** The plan's name for people: its title, else the booking's event and
 *  customer, else its number. */
export function planName(p: { title?: string | null; plan_no: string; booking?: { customer_name: string; event_name?: string | null } | null }): string {
  if (p.title?.trim()) return p.title.trim()
  if (p.booking) return p.booking.event_name?.trim() ? `${p.booking.event_name.trim()} — ${p.booking.customer_name}` : p.booking.customer_name
  return p.plan_no
}

/** Whole days from a to b (YYYY-MM-DD, b − a). */
export function daysBetween(a: string, b: string): number {
  const toUTC = (s: string) => {
    const [y, m, d] = s.slice(0, 10).split('-').map(Number)
    return Date.UTC(y, (m || 1) - 1, d || 1)
  }
  return Math.round((toUTC(b) - toUTC(a)) / 86_400_000)
}

/** YYYY-MM-DD plus n days. */
export function addDays(day: string, n: number): string {
  const [y, m, d] = day.slice(0, 10).split('-').map(Number)
  const t = new Date(Date.UTC(y, (m || 1) - 1, (d || 1) + n))
  return t.toISOString().slice(0, 10)
}

/** "19:30" / "7:30 pm" → "7:30 PM"; anything else as typed. */
export function fmtEventTime(t?: string | null): string {
  const s = (t ?? '').trim()
  const m = /^(\d{1,2}):(\d{2})(?::\d{2})?\s*(am|pm)?$/i.exec(s)
  if (!m) return s
  let h = Number(m[1])
  const ap = m[3]?.toLowerCase()
  if (ap === 'pm' && h < 12) h += 12
  if (ap === 'am' && h === 12) h = 0
  if (h > 23) return s
  return `${h % 12 === 0 ? 12 : h % 12}:${m[2]} ${h < 12 ? 'AM' : 'PM'}`
}

// ── Needs you now ───────────────────────────────────────────────────────────

export type NeedItem =
  | { kind: 'arrival'; key: string; reqId: string; reqNo: string; planTitle: string | null; planDate: string | null; items: number; sentAt: string | null; sentBy: string | null }
  | { kind: 'short'; key: string; reqId: string; reqNo: string; planTitle: string | null; items: number }
  | { kind: 'overdue'; key: string; planId: string; name: string; days: number }
  | { kind: 'basics'; key: string; names: string[]; lines: { stock_item_id: string; qty: number }[] }

/** A sent plan whose day has passed and that was never wrapped up. A plan
 *  never sent drew nothing from the store, so there is nothing to wrap up. */
export function isOverdue(p: Pick<KitchenTodayPlan, 'status' | 'for_date' | 'sent_at'>, today: string): boolean {
  if (p.status === 'cancelled' || p.status === 'draft' || p.status === 'confirmed') return false
  return p.for_date < today
}

/** The basics ask: every low basic at its top-up. */
export function basicsAskLines(rows: KitchenTodayBasic[]): { stock_item_id: string; qty: number }[] {
  return rows.filter((r) => r.top_up > 1e-6).map((r) => ({ stock_item_id: r.stock_item_id, qty: r.top_up }))
}

/** What needs the chef now, most urgent first: stock waiting to be checked
 *  in (newest first), stock missing on the way, events never wrapped up
 *  (oldest first), then kitchen basics running low. */
export function needsYouNow(t: KitchenToday): NeedItem[] {
  const out: NeedItem[] = []
  for (const a of t.arrivals) {
    out.push({ kind: 'arrival', key: `arrival-${a.id}`, reqId: a.id, reqNo: a.req_no, planTitle: a.plan_title?.trim() || a.plan_no || null,
      planDate: a.plan_for_date ?? null, items: a.items, sentAt: a.sent_at ?? null, sentBy: a.sent_by_name ?? null })
  }
  for (const s of t.shortages) {
    out.push({ kind: 'short', key: `short-${s.id}`, reqId: s.id, reqNo: s.req_no, planTitle: s.plan_title?.trim() || null, items: s.items })
  }
  const overdue = t.plans.filter((p) => isOverdue(p, t.today)).sort((a, b) => a.for_date.localeCompare(b.for_date))
  for (const p of overdue) out.push({ kind: 'overdue', key: `overdue-${p.id}`, planId: p.id, name: planName(p), days: daysBetween(p.for_date, t.today) })
  const lines = basicsAskLines(t.basics_low)
  if (lines.length > 0) {
    out.push({ kind: 'basics', key: 'basics', names: t.basics_low.filter((b) => b.top_up > 1e-6).map((b) => b.name), lines })
  }
  return out
}

// ── Coming up ───────────────────────────────────────────────────────────────

/** Plans from today to `days` ahead, soonest first (by date, then event time). */
export function comingUp(t: KitchenToday, days = 7): KitchenTodayPlan[] {
  const last = addDays(t.today, days)
  return t.plans
    .filter((p) => p.status !== 'cancelled' && p.for_date >= t.today && p.for_date <= last)
    .sort((a, b) => a.for_date.localeCompare(b.for_date) || sortableTime(a.booking?.event_time).localeCompare(sortableTime(b.booking?.event_time)))
}

function sortableTime(t?: string | null): string {
  const f = fmtEventTime(t)
  const m = /^(\d{1,2}):(\d{2}) (AM|PM)$/.exec(f)
  if (!m) return '99:99'
  const h = (Number(m[1]) % 12) + (m[3] === 'PM' ? 12 : 0)
  return `${String(h).padStart(2, '0')}:${m[2]}`
}

/** The summary row as a step input. */
export function stepInputOf(p: KitchenTodayPlan): StepInput & { for_date: string } {
  return { status: p.status, sent_at: p.sent_at, wrapped: false, requisition_id: p.requisition_id, requisition_status: p.requisition_status, request: p.request, for_date: p.for_date }
}

/**
 * The one-line status under a plan. A shape, not words (todayStrings.ts
 * words it), so it can be tested:
 *  notLocked                 a draft
 *  notAsked                  locked (or cooking) and the store hasn't been asked
 *  nothingAsked              sent; the kitchen had everything
 *  waiting   {total}         asked, nothing sent yet
 *  partly    {sent,total,toCome}
 *  arrived   {items}         stock sent, waiting to be checked in
 *  here      {here,total}
 *  refused                   the store turned it down
 *  cooking / cooked
 */
export type StatusLine =
  | { kind: 'notLocked' }
  | { kind: 'notAsked'; cooking: boolean }
  | { kind: 'nothingAsked' }
  | { kind: 'waiting'; total: number }
  | { kind: 'partly'; sent: number; total: number; toCome: number }
  | { kind: 'arrived'; items: number | null }
  | { kind: 'here'; here: number; total: number | null }
  | { kind: 'refused' }
  | { kind: 'cooking' }
  | { kind: 'cooked' }

export function statusLine(p: StepInput, arrivalItems?: number): StatusLine {
  if (p.status === 'draft') return { kind: 'notLocked' }
  if (p.status === 'done') return { kind: 'cooked' }
  const stock = stockState(p)
  if (stock === 'none') return { kind: 'notAsked', cooking: p.status === 'in_production' }
  if (p.status === 'in_production') return { kind: 'cooking' }
  const r = p.request
  const total = r ? r.lines - r.not_coming : 0
  switch (stock) {
    case 'nothing-asked':
      return { kind: 'nothingAsked' }
    case 'refused':
      return { kind: 'refused' }
    case 'arrived':
      return { kind: 'arrived', items: arrivalItems ?? null }
    case 'here':
      return { kind: 'here', here: r ? r.here : 0, total: r ? total : null }
    case 'waiting':
      return { kind: 'waiting', total: r ? total : 0 }
    case 'partly':
      return { kind: 'partly', sent: r!.sent, total, toCome: Math.max(0, total - r!.sent) }
    default:
      return { kind: 'notLocked' }
  }
}

// ── The plan view ───────────────────────────────────────────────────────────

/** "Next: ask the store for 9 items — the kitchen already has enough of 4
 *  more": how many raw items will be asked for (amount > 0) and how many the
 *  kitchen already covers. */
export function askCounts(demand: { stock_item_id: string; is_prep: boolean }[], asks: Record<string, number>): { ask: number; have: number } {
  let ask = 0
  let have = 0
  for (const d of demand) {
    if (d.is_prep) continue
    if ((asks[d.stock_item_id] ?? 0) > 1e-6) ask++
    else have++
  }
  return { ask, have }
}

/** Long lists show the first `limit` rows until "Show all" is pressed. */
export function firstRows<T>(rows: T[], showAll: boolean, limit = 8): { shown: T[]; hidden: number } {
  if (showAll || rows.length <= limit + 1) return { shown: rows, hidden: 0 }
  return { shown: rows.slice(0, limit), hidden: rows.length - limit }
}

// ── The page's tabs ─────────────────────────────────────────────────────────

/** The three tabs, and the tools the header opens. */
export type ProductionView = 'today' | 'events' | 'requests' | 'ask' | 'return' | 'make' | 'count' | 'waste' | 'basics'

const VIEW_ALIASES: Record<string, ProductionView> = {
  today: 'today',
  events: 'events',
  plan: 'events',
  plans: 'events',
  requests: 'requests',
  mine: 'requests',
  ask: 'ask',
  request: 'ask',
  return: 'return',
  make: 'make',
  count: 'count',
  waste: 'waste',
  basics: 'basics',
}

/** Where a link lands: ?plan= opens the plan under Events; an old ?tab=
 *  name maps to its new place; otherwise the role's landing tab (Today for
 *  the kitchen manager and admin, Requests for anyone else). */
export function initialView(search: { tab?: string; plan?: string }, role: string): ProductionView {
  if (search.plan) return 'events'
  const v = search.tab ? VIEW_ALIASES[search.tab.toLowerCase()] : undefined
  if (v) return v
  return role === 'kitchen_manager' || role === 'admin' ? 'today' : 'requests'
}

export const isTabView = (v: ProductionView) => v === 'today' || v === 'events' || v === 'requests'
